import { apiClient } from "@/services/apiClient";

export type OobBulkMode = "AddNew" | "ReplaceAll" | "Merge";

export interface OobParticipantDto {
  participantGuid: string;
  employeeId: string;
  email: string;
  displayName: string;
  department?: string;
  location?: string;
  region?: string;
  phoneNumber?: string;
  personalEmail?: string;
  personalPhone?: string;
  preferredContactMethod?: string;
  personalEmailVerified?: boolean;
  personalPhoneVerified?: boolean;
  readinessState?: string;
  onboardingInviteSentDt?: string;
  secondaryGuestObjectId?: string;
  secondaryIdentityStatus?: string;
  secondaryIdentityVerifiedDt?: string;
  lastSuccessfulOobTestDt?: string;
  secondaryIdentityLastError?: string;
  isActive: boolean;
  isDefaultParticipant: boolean;
  groupGuids: string[];
  lastReviewDt?: string;
  annualReviewDt?: string;
  createDt: string;
  lastUpdateDt: string;
}

export interface OobEmployeeLookupDto {
  employeeId: string;
  email: string;
  displayName: string;
  department?: string;
  jobTitle?: string;
}

export interface OobCreateParticipantRequest {
  employeeId: string;
  email: string;
  displayName: string;
  department?: string;
  location?: string;
  region?: string;
  phoneNumber?: string;
  isDefaultParticipant: boolean;
  groupGuids: string[];
}

export interface OobUpdateParticipantRequest extends OobCreateParticipantRequest {
  isActive?: boolean;
  lastReviewDt?: string;
  annualReviewDt?: string;
}

export interface OobUpdateParticipantGroupsRequest {
  groupGuids: string[];
}

export interface OobBulkImportError {
  row: number;
  message: string;
}

export interface OobBulkImportResult {
  totalRows: number;
  succeeded: number;
  failed: number;
  errors: OobBulkImportError[];
}

export interface OobPurgeResult {
  purgedCount: number;
  timestamp: string;
}

export interface OobPurgeGroupRequest {
  purgeParticipants: boolean;
  archiveGroup: boolean;
}

export interface OobPurgeGroupResult {
  groupGuid: string;
  purgedParticipants: number;
  purgedMemberships: number;
  groupArchived: boolean;
  timestamp: string;
}

export interface OobAdminEmergencyProfileDto {
  email: string;
  emergencyEmail?: string;
  verifiedPhone?: string;
  isConfigured: boolean;
  hasSuccessfulBreakGlassTest: boolean;
  emergencyAccessConfiguredDt?: string;
  lastSuccessfulBreakGlassTestDt?: string;
}

export interface OobUpdateAdminEmergencyProfileRequest {
  emergencyEmail: string;
  verifiedPhone: string;
}

export interface OobPurgeAllRequest {
  purgeParticipants: boolean;
  purgeGroups: boolean;
}

export interface OobPurgeAllResult {
  purgedParticipants: number;
  purgedGroups: number;
  purgedGroupMemberships: number;
  timestamp: string;
}

export interface OobCreateEventRequest {
  eventName: string;
  eventType: "Test" | "Onboarding" | "Activation" | "AnnualReview";
  safetyPhrase: string;
  description?: string;
  createLocalEvent: boolean;
  groupGuids: string[];
  isSilent: boolean;
}

export interface OobCreateUpdateRequest {
  messageTitle: string;
  messageBody: string;
  importance: "Normal" | "Important" | "Critical";
  isPinned: boolean;
}

export interface OobEditUpdateRequest {
  messageTitle: string;
  messageBody: string;
  importance: "Normal" | "Important" | "Critical";
}

export interface OobStatusUpdateDto {
  updateGuid: string;
  eventGuid: string;
  messageTitle: string;
  messageBody: string;
  linkUrl?: string;
  linkLabel?: string;
  importance: string;
  isPinned: boolean;
  pinnedOrder: number;
  modifiedBy?: string;
  modifiedDt?: string;
  retractedDt?: string;
  effEndDt?: string;
  createDt: string;
  acknowledgementCount: number;
}

export interface OobEventDto {
  eventGuid: string;
  eventName: string;
  eventType: string;
  status: string;
  deliveryMode: string;
  deliveryStatus: string;
  deliveryError?: string;
  deliveryAttemptDt?: string;
  localActivatedBy?: string;
  localActivatedDt?: string;
  deliveryCommittedDt?: string;
  isSilent: boolean;
  safetyPhrase: string;
  description?: string;
  createdBy: string;
  activatedBy?: string;
  closedBy?: string;
  createDt: string;
  activatedDt?: string;
  closedDt?: string;
  resolvedBy?: string;
  resolvedDt?: string;
  groupGuids: string[];
  updates: OobStatusUpdateDto[];
}

export interface OobEventActivationResultDto {
  event: OobEventDto;
  deliveryStatus: string;
  requiresLocalPivot: boolean;
  error?: string;
}

export interface OobUpdatePivotEventRequest extends OobCreateEventRequest {
}

export interface OobSecondaryProvisioningStatusDto {
  eventGuid: string;
  status: string;
  teamId?: string;
  teamDisplayName?: string;
  lastAttemptDt?: string;
  provisionedDt?: string;
  error?: string;
  participantProvisionedCount: number;
  participantFailureCount: number;
  artifactProvisionedCount: number;
  artifactFailureCount: number;
  messagePublishedCount: number;
  messageFailureCount: number;
}

export interface OobSecondaryFollowUpDto {
  followUpGuid: string;
  eventGuid: string;
  participantGuid?: string;
  guestObjectId?: string;
  participantEmail?: string;
  exceptionType: string;
  description: string;
  status: string;
  resolvedBy?: string;
  resolvedDt?: string;
  resolutionNote?: string;
  createDt: string;
}

export interface OobAuditEventDto {
  outOfBandAuditEventId: number;
  eventGuid?: string;
  participantGuid?: string;
  updateGuid?: string;
  auditType: string;
  severity: string;
  message: string;
  actionUser: string;
  actionTimestamp: string;
  payloadJson?: string;
}

export interface OobAcknowledgementSummaryDto {
  participantGuid: string;
  email: string;
  displayName: string;
  acknowledgementCount: number;
  lastAcknowledgedDt?: string;
}

export interface OobAcknowledgementRequest {
  participantEmail: string;
  updateGuid?: string;
  ackSource: "Portal" | "SmsLink" | "EmailLink";
  optionalComment?: string;
}

export interface OobAcknowledgementDto {
  ackGuid: string;
  eventGuid: string;
  participantGuid: string;
  updateGuid?: string;
  ackSource: string;
  optionalComment?: string;
  acknowledgedDt: string;
}

export interface OobSecondaryIdentityConfirmationDto {
  eventGuid: string;
  participantGuid: string;
  secondaryIdentityStatus: string;
  confirmedDt: string;
}

export interface OobSecondaryIdentityInviteDto {
  participantGuid: string;
  email: string;
  identityStatus: string;
  guestObjectId?: string;
  inviteSentDt: string;
}

export interface OobParticipantBoardDto {
  event?: OobEventDto;
  hasAcknowledged: boolean;
  lastAcknowledgedDt?: string;
  includedParticipantCount: number;
  acknowledgedParticipantCount: number;
}

export interface OobEventQuestionDto {
  questionGuid: string;
  eventGuid: string;
  participantGuid: string;
  questionText: string;
  answerText?: string;
  displayAsAnonymous: boolean;
  isAnswered: boolean;
  isPublished: boolean;
  importance: string;
  publishedUpdateGuid?: string;
  answeredBy?: string;
  answeredDt?: string;
  publishedBy?: string;
  publishedDt?: string;
  createDt: string;
}

export interface OobCreateEventQuestionRequest {
  questionText: string;
  displayAsAnonymous: boolean;
}

export interface OobAnswerEventQuestionRequest {
  answerText: string;
  importance: "Normal" | "Important" | "Critical";
  isPinned: boolean;
}

export interface OobEditEventQuestionRequest {
  questionText: string;
  answerText?: string;
  displayAsAnonymous: boolean;
  importance: "Normal" | "Important" | "Critical";
}

export interface OobLocalEventChannelDto {
  channelGuid: string;
  eventGuid: string;
  channelKey: string;
  displayName: string;
  description?: string;
  audienceScope: string;
  postingPolicy: string;
  sortOrder: number;
  messageCount: number;
  lastMessageDt?: string;
}

export interface OobLocalEventMessageDto {
  messageGuid: string;
  eventGuid: string;
  channelGuid: string;
  participantGuid?: string;
  authorEmail: string;
  authorDisplayName?: string;
  authorRole: string;
  messageBody: string;
  createDt: string;
}

export interface OobCreateLocalEventMessageRequest {
  messageBody: string;
}

export interface OobEventParticipantDto {
  eventGuid: string;
  participantGuid: string;
  email: string;
  displayName: string;
  createDt: string;
}

export interface OobParticipantGroupDto {
  groupGuid: string;
  groupName: string;
  sortOrder: number;
  description?: string;
  memberCount: number;
}

export interface OobCreateParticipantGroupRequest {
  groupName: string;
  sortOrder: number;
  description?: string;
}

export interface OobParticipantGroupMemberDto {
  groupGuid: string;
  participantGuid: string;
  email: string;
  displayName: string;
  department?: string;
  isActive: boolean;
}

export interface OobCreatePlatformAnnouncementRequest {
  title: string;
  message: string;
  groupGuids: string[];
  includeParticipantGuids: string[];
  excludeParticipantGuids: string[];
}

export interface OobPlatformAnnouncementResult {
  recipientCount: number;
  queuedCount: number;
}

export interface OobHomeEventSummaryDto {
  eventGuid: string;
  eventName: string;
  eventType: string;
  status: string;
  createdDt: string;
  activatedDt?: string;
  updateCount: number;
  acknowledgementCount: number;
}

export interface OobUserHomeDto {
  title: string;
  subtitle: string;
  activeEvents: OobHomeEventSummaryDto[];
  totalActiveEvents: number;
  portalUrl: string;
  generatedDt: string;
}

export interface OobPlatformAnnouncementDto {
  announcementGuid: string;
  title: string;
  message: string;
  createdBy: string;
  recipientCount: number;
  queuedCount: number;
  createDt: string;
}

export interface OobPlatformAnnouncementListDto {
  announcements: OobPlatformAnnouncementDto[];
  totalCount: number;
}

export interface OobContactSettingsDto {
  participantGuid: string;
  email: string;
  displayName: string;
  personalEmail?: string;
  personalPhone?: string;
  preferredContactMethod: "Email" | "Text";
  personalEmailVerified: boolean;
  personalPhoneVerified: boolean;
  readinessState: string;
  secondaryGuestObjectId?: string;
  secondaryIdentityStatus: string;
  secondaryIdentityVerifiedDt?: string;
  lastSuccessfulOobTestDt?: string;
  secondaryIdentityLastError?: string;
  isOnboardingComplete: boolean;
}

export interface OobUpdateContactSettingsRequest {
  personalPhone: string;
  personalEmail: string;
  preferredContactMethod: "Email" | "Text";
}

export interface OobSendContactVerificationCodeRequest {
  channel: "Email" | "Text";
}

export interface OobVerifyContactVerificationCodeRequest {
  channel: "Email" | "Text";
  code: string;
}

export interface OobContactVerificationResultDto {
  success: boolean;
  message: string;
  settings?: OobContactSettingsDto;
}

export interface OobReadinessWindowStatsDto {
  label: string;
  addedCount: number;
  verifiedCount: number;
  verifiedPercent: number;
}

export interface OobParticipantReadinessRowDto {
  participantGuid: string;
  displayName: string;
  email: string;
  createDt: string;
  lastUpdateDt: string;
  readinessState: string;
  preferredContactMethod: string;
  personalEmail?: string;
  personalEmailVerified: boolean;
  personalPhoneVerified: boolean;
  onboardingInviteSentDt?: string;
  secondaryGuestObjectId?: string;
  secondaryIdentityStatus: string;
  secondaryIdentityLastError?: string;
  groupCount: number;
}

export interface OobReadinessDashboardDto {
  thisWeek: OobReadinessWindowStatsDto;
  thisMonth: OobReadinessWindowStatsDto;
  thisQuarter: OobReadinessWindowStatsDto;
  totalParticipants: number;
  totalFullyVerified: number;
  totalFullyVerifiedPercent: number;
  participants: OobParticipantReadinessRowDto[];
}

export interface OobArtifactLibraryItemDto {
  artifactGuid: string;
  artifactName: string;
  artifactCategory: string;
  storageProvider: string;
  relativePath: string;
  downloadUrl: string;
  contentType?: string;
  fileSizeBytes?: number;
  sha256?: string;
  isActive: boolean;
  createDt: string;
  lastUpdateDt: string;
}

export interface OobEventArtifactDto {
  artifactGuid: string;
  eventGuid: string;
  artifactName: string;
  artifactType: string;
  storageProvider: string;
  storageContainer?: string;
  storagePath?: string;
  downloadUrl?: string;
  contentType?: string;
  fileSizeBytes?: number;
  sha256?: string;
  isRequired: boolean;
  sortOrder: number;
  provisioningStatus: string;
  provisionedDt?: string;
  createDt: string;
  lastUpdateDt: string;
}

export interface OobEventArtifactManifestDto {
  manifestGuid: string;
  eventGuid: string;
  manifestVersion: string;
  artifactCount: number;
  manifestJson: string;
  generatedBy: string;
  generatedDt: string;
}

export async function getOobHome(): Promise<OobUserHomeDto> {
  const { data } = await apiClient.get<OobUserHomeDto>("/api/oob/home");
  return data;
}

export async function getOobAnnouncements(skip: number = 0, take: number = 50): Promise<OobPlatformAnnouncementListDto> {
  const { data } = await apiClient.get<OobPlatformAnnouncementListDto>("/api/oob/announcements", {
    params: { skip, take }
  });
  return data;
}

export async function getOobContactSettings(): Promise<OobContactSettingsDto> {
  const { data } = await apiClient.get<OobContactSettingsDto>("/api/oob/profile/contact-settings");
  return data;
}

export async function updateOobContactSettings(payload: OobUpdateContactSettingsRequest): Promise<OobContactSettingsDto> {
  const { data } = await apiClient.put<OobContactSettingsDto>("/api/oob/profile/contact-settings", payload);
  return data;
}

export async function sendOobContactVerificationCode(payload: OobSendContactVerificationCodeRequest): Promise<OobContactVerificationResultDto> {
  const { data } = await apiClient.post<OobContactVerificationResultDto>("/api/oob/profile/contact-settings/send-code", payload);
  return data;
}

export async function verifyOobContactVerificationCode(payload: OobVerifyContactVerificationCodeRequest): Promise<OobContactVerificationResultDto> {
  const { data } = await apiClient.post<OobContactVerificationResultDto>("/api/oob/profile/contact-settings/verify-code", payload);
  return data;
}

export async function getOobParticipants(): Promise<OobParticipantDto[]> {
  const { data } = await apiClient.get<OobParticipantDto[]>("/api/admin/oob/participants");
  return data;
}

export async function searchOobEmployeeDirectory(search: string): Promise<OobEmployeeLookupDto[]> {
  const { data } = await apiClient.get<OobEmployeeLookupDto[]>("/api/admin/oob/employee-directory", { params: { search } });
  return data;
}

export async function getOobReadinessDashboard(): Promise<OobReadinessDashboardDto> {
  const { data } = await apiClient.get<OobReadinessDashboardDto>("/api/admin/oob/participants/readiness-dashboard");
  return data;
}

export async function getOobArtifactLibrary(): Promise<OobArtifactLibraryItemDto[]> {
  const { data } = await apiClient.get<OobArtifactLibraryItemDto[]>("/api/admin/oob/artifacts/library");
  return data;
}

export async function uploadOobArtifactToLibrary(file: File, category: string = "RunBook"): Promise<OobArtifactLibraryItemDto> {
  const form = new FormData();
  form.append("file", file);
  form.append("category", category);

  const { data } = await apiClient.post<OobArtifactLibraryItemDto>("/api/admin/oob/artifacts/library/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return data;
}

export async function archiveOobArtifactFromLibrary(artifactGuid: string): Promise<void> {
  await apiClient.delete(`/api/admin/oob/artifacts/library/${artifactGuid}`);
}

export async function downloadOobLibraryArtifact(artifact: OobArtifactLibraryItemDto): Promise<void> {
  const storedName = artifact.relativePath.split("/").pop() || artifact.artifactName;
  const storedPrefix = `${artifact.artifactGuid.replace(/-/g, "")}_`;
  const fileName = storedName.toLowerCase().startsWith(storedPrefix.toLowerCase())
    ? storedName.slice(storedPrefix.length)
    : storedName;
  await downloadOobArtifact(`/api/admin/oob/artifacts/library/${artifact.artifactGuid}/download`, fileName);
}

export async function getOobEventArtifacts(eventGuid: string): Promise<OobEventArtifactDto[]> {
  const { data } = await apiClient.get<OobEventArtifactDto[]>(`/api/admin/oob/events/${eventGuid}/artifacts`);
  return data;
}

export async function getOobLatestEventArtifactManifest(eventGuid: string): Promise<OobEventArtifactManifestDto> {
  const { data } = await apiClient.get<OobEventArtifactManifestDto>(`/api/admin/oob/events/${eventGuid}/artifacts/manifest/latest`);
  return data;
}

export async function createOobParticipant(payload: OobCreateParticipantRequest): Promise<OobParticipantDto> {
  const { data } = await apiClient.post<OobParticipantDto>("/api/admin/oob/participants", payload);
  return data;
}

export async function inviteOobSecondaryIdentity(participantGuid: string): Promise<OobSecondaryIdentityInviteDto> {
  const { data } = await apiClient.post<OobSecondaryIdentityInviteDto>(
    `/api/admin/oob/participants/${participantGuid}/secondary-identity/invite`
  );
  return data;
}

export async function updateOobParticipant(participantGuid: string, payload: OobUpdateParticipantRequest): Promise<OobParticipantDto> {
  const { data } = await apiClient.put<OobParticipantDto>(`/api/admin/oob/participants/${participantGuid}`, payload);
  return data;
}

export async function updateOobParticipantGroups(participantGuid: string, payload: OobUpdateParticipantGroupsRequest): Promise<void> {
  await apiClient.put(`/api/admin/oob/participants/${participantGuid}/groups`, payload);
}

export async function removeOobParticipantFromGroup(groupGuid: string, participantGuid: string): Promise<void> {
  await apiClient.delete(`/api/admin/oob/groups/${groupGuid}/members/${participantGuid}`);
}

export async function archiveOobParticipant(participantGuid: string): Promise<void> {
  await apiClient.delete(`/api/admin/oob/participants/${participantGuid}`);
}

export async function purgeOobParticipants(): Promise<OobPurgeResult> {
  const { data } = await apiClient.post<OobPurgeResult>("/api/admin/oob/participants/purge-all?confirm=true");
  return data;
}

export async function purgeOobGroup(groupGuid: string, payload: OobPurgeGroupRequest): Promise<OobPurgeGroupResult> {
  const { data } = await apiClient.post<OobPurgeGroupResult>(`/api/admin/oob/groups/${groupGuid}/purge`, payload);
  return data;
}

export async function purgeOobAll(payload: OobPurgeAllRequest): Promise<OobPurgeAllResult> {
  const { data } = await apiClient.post<OobPurgeAllResult>("/api/admin/oob/purge", payload);
  return data;
}

export async function bulkImportOobParticipants(file: File, mode: OobBulkMode, targetGroupGuid: string): Promise<OobBulkImportResult> {
  const form = new FormData();
  form.append("file", file);
  form.append("mode", mode);
  form.append("targetGroupGuid", targetGroupGuid);

  const { data } = await apiClient.post<OobBulkImportResult>("/api/admin/oob/participants/bulk-import", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function getOobEvents(status?: string): Promise<OobEventDto[]> {
  const { data } = await apiClient.get<OobEventDto[]>("/api/admin/oob/events", { params: { status } });
  return data;
}

export async function createOobEvent(payload: OobCreateEventRequest): Promise<OobEventDto> {
  const { data } = await apiClient.post<OobEventDto>("/api/admin/oob/events", payload);
  return data;
}

export async function getOobEvent(eventGuid: string, includeArchived = false): Promise<OobEventDto> {
  const { data } = await apiClient.get<OobEventDto>(`/api/admin/oob/events/${eventGuid}`, {
    params: { includeArchived },
  });
  return data;
}

export async function activateOobEvent(eventGuid: string): Promise<OobEventActivationResultDto> {
  const { data } = await apiClient.post<OobEventActivationResultDto>(`/api/admin/oob/events/${eventGuid}/activate`);
  return data;
}

export async function activateOobLocalEventSpace(eventGuid: string): Promise<OobEventActivationResultDto> {
  const { data } = await apiClient.post<OobEventActivationResultDto>(`/api/admin/oob/events/${eventGuid}/activate-local`);
  return data;
}

export async function updateOobPivotEvent(eventGuid: string, payload: OobUpdatePivotEventRequest): Promise<OobEventDto> {
  const { data } = await apiClient.put<OobEventDto>(`/api/admin/oob/events/${eventGuid}/pivot-review`, payload);
  return data;
}

export async function closeOobEvent(eventGuid: string): Promise<void> {
  await apiClient.post(`/api/admin/oob/events/${eventGuid}/close`);
}

export async function resolveOobEvent(eventGuid: string): Promise<void> {
  await apiClient.post(`/api/admin/oob/events/${eventGuid}/resolve`);
}

export async function getOobSecondaryProvisioningStatus(eventGuid: string): Promise<OobSecondaryProvisioningStatusDto> {
  const { data } = await apiClient.get<OobSecondaryProvisioningStatusDto>(
    `/api/admin/oob/events/${eventGuid}/secondary-provisioning`
  );
  return data;
}

export async function retryOobSecondaryProvisioning(eventGuid: string): Promise<OobSecondaryProvisioningStatusDto> {
  const { data } = await apiClient.post<OobSecondaryProvisioningStatusDto>(
    `/api/admin/oob/events/${eventGuid}/secondary-provisioning/retry`
  );
  return data;
}

export async function reconcileOobEventMembership(eventGuid: string): Promise<OobSecondaryFollowUpDto[]> {
  const { data } = await apiClient.post<OobSecondaryFollowUpDto[]>(
    `/api/admin/oob/events/${eventGuid}/reconcile-membership`
  );
  return data;
}

export async function getOobEventFollowUps(eventGuid: string): Promise<OobSecondaryFollowUpDto[]> {
  const { data } = await apiClient.get<OobSecondaryFollowUpDto[]>(
    `/api/admin/oob/events/${eventGuid}/follow-ups`
  );
  return data;
}

export async function resolveOobEventFollowUp(eventGuid: string, followUpGuid: string, resolutionNote?: string): Promise<void> {
  await apiClient.post(`/api/admin/oob/events/${eventGuid}/follow-ups/${followUpGuid}/resolve`, { resolutionNote });
}

export async function finalizeOobEvent(eventGuid: string): Promise<void> {
  await apiClient.post(`/api/admin/oob/events/${eventGuid}/finalize`);
}

export async function deprovisionOobEvent(eventGuid: string): Promise<void> {
  await apiClient.post(`/api/admin/oob/events/${eventGuid}/deprovision`);
}

export async function postOobUpdate(eventGuid: string, payload: OobCreateUpdateRequest): Promise<OobStatusUpdateDto> {
  const { data } = await apiClient.post<OobStatusUpdateDto>(`/api/admin/oob/events/${eventGuid}/updates`, payload);
  return data;
}

export async function editOobUpdate(updateGuid: string, payload: OobEditUpdateRequest): Promise<void> {
  await apiClient.put(`/api/admin/oob/updates/${updateGuid}`, payload);
}

export async function pinOobUpdate(updateGuid: string, isPinned = true): Promise<void> {
  await apiClient.post(`/api/admin/oob/updates/${updateGuid}/pin`, null, { params: { isPinned } });
}

export async function retractOobUpdate(updateGuid: string): Promise<void> {
  await apiClient.post(`/api/admin/oob/updates/${updateGuid}/retract`);
}

export async function unretractOobUpdate(updateGuid: string): Promise<void> {
  await apiClient.post(`/api/admin/oob/updates/${updateGuid}/unretract`);
}

export async function archiveOobUpdate(updateGuid: string): Promise<void> {
  await apiClient.post(`/api/admin/oob/updates/${updateGuid}/archive`);
}

export async function getOobAcknowledgementSummary(eventGuid: string): Promise<OobAcknowledgementSummaryDto[]> {
  const { data } = await apiClient.get<OobAcknowledgementSummaryDto[]>(`/api/admin/oob/events/${eventGuid}/acks`);
  return data;
}

export async function getOobAudit(eventGuid: string, skip = 0, take = 200): Promise<OobAuditEventDto[]> {
  const { data } = await apiClient.get<OobAuditEventDto[]>(`/api/admin/oob/events/${eventGuid}/audit`, {
    params: { skip, take },
  });
  return data;
}

export async function resendOobPending(eventGuid: string): Promise<{ queuedCount: number }> {
  const { data } = await apiClient.post<{ queuedCount: number }>(`/api/admin/oob/events/${eventGuid}/resend-pending`);
  return data;
}

export async function getOobParticipantBoard(eventGuid: string): Promise<OobParticipantBoardDto> {
  const { data } = await apiClient.get<OobParticipantBoardDto>(`/api/oob/${eventGuid}`);
  return data;
}

export async function getOobParticipantEventArtifacts(eventGuid: string): Promise<OobEventArtifactDto[]> {
  const { data } = await apiClient.get<OobEventArtifactDto[]>(`/api/oob/${eventGuid}/artifacts`);
  return data;
}

async function downloadOobArtifact(url: string, fileName: string): Promise<void> {
  const { data } = await apiClient.get<Blob>(url, { responseType: "blob" });
  const objectUrl = URL.createObjectURL(data);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

export async function downloadOobParticipantEventArtifact(eventGuid: string, artifactGuid: string, fileName: string): Promise<void> {
  await downloadOobArtifact(`/api/oob/${eventGuid}/artifacts/${artifactGuid}/download`, fileName);
}

export async function downloadOobAdminEventArtifact(eventGuid: string, artifactGuid: string, fileName: string): Promise<void> {
  await downloadOobArtifact(`/api/admin/oob/events/${eventGuid}/artifacts/${artifactGuid}/download`, fileName);
}

export async function getOobParticipantEventQuestions(eventGuid: string): Promise<OobEventQuestionDto[]> {
  const { data } = await apiClient.get<OobEventQuestionDto[]>(`/api/oob/${eventGuid}/questions`);
  return data;
}

export async function submitOobParticipantEventQuestion(eventGuid: string, payload: OobCreateEventQuestionRequest): Promise<OobEventQuestionDto> {
  const { data } = await apiClient.post<OobEventQuestionDto>(`/api/oob/${eventGuid}/questions`, payload);
  return data;
}

export async function getOobAdminEventQuestions(eventGuid: string): Promise<OobEventQuestionDto[]> {
  const { data } = await apiClient.get<OobEventQuestionDto[]>(`/api/admin/oob/events/${eventGuid}/questions`);
  return data;
}

export async function publishOobAdminEventQuestion(eventGuid: string, questionGuid: string, payload: OobAnswerEventQuestionRequest): Promise<OobEventQuestionDto> {
  const { data } = await apiClient.post<OobEventQuestionDto>(`/api/admin/oob/events/${eventGuid}/questions/${questionGuid}/publish`, payload);
  return data;
}

export async function editOobAdminEventQuestion(eventGuid: string, questionGuid: string, payload: OobEditEventQuestionRequest): Promise<OobEventQuestionDto> {
  const { data } = await apiClient.put<OobEventQuestionDto>(`/api/admin/oob/events/${eventGuid}/questions/${questionGuid}`, payload);
  return data;
}

export async function getOobAdminLocalEventChannels(eventGuid: string): Promise<OobLocalEventChannelDto[]> {
  const { data } = await apiClient.get<OobLocalEventChannelDto[]>(`/api/admin/oob/events/${eventGuid}/local-channels`);
  return data;
}

export async function getOobAdminLocalEventChannelMessages(eventGuid: string, channelGuid: string): Promise<OobLocalEventMessageDto[]> {
  const { data } = await apiClient.get<OobLocalEventMessageDto[]>(`/api/admin/oob/events/${eventGuid}/local-channels/${channelGuid}/messages`);
  return data;
}

export async function postOobAdminLocalEventChannelMessage(eventGuid: string, channelGuid: string, payload: OobCreateLocalEventMessageRequest): Promise<OobLocalEventMessageDto> {
  const { data } = await apiClient.post<OobLocalEventMessageDto>(`/api/admin/oob/events/${eventGuid}/local-channels/${channelGuid}/messages`, payload);
  return data;
}

export async function getOobParticipantLocalEventChannels(eventGuid: string): Promise<OobLocalEventChannelDto[]> {
  const { data } = await apiClient.get<OobLocalEventChannelDto[]>(`/api/oob/${eventGuid}/local-channels`);
  return data;
}

export async function getOobParticipantLocalEventChannelMessages(eventGuid: string, channelGuid: string): Promise<OobLocalEventMessageDto[]> {
  const { data } = await apiClient.get<OobLocalEventMessageDto[]>(`/api/oob/${eventGuid}/local-channels/${channelGuid}/messages`);
  return data;
}

export async function postOobParticipantLocalEventChannelMessage(eventGuid: string, channelGuid: string, payload: OobCreateLocalEventMessageRequest): Promise<OobLocalEventMessageDto> {
  const { data } = await apiClient.post<OobLocalEventMessageDto>(`/api/oob/${eventGuid}/local-channels/${channelGuid}/messages`, payload);
  return data;
}

export async function acknowledgeOobEvent(eventGuid: string, payload: OobAcknowledgementRequest): Promise<OobAcknowledgementDto> {
  const { data } = await apiClient.post<OobAcknowledgementDto>(`/api/oob/${eventGuid}/acknowledge`, payload);
  return data;
}

export async function confirmOobSecondaryIdentity(eventGuid: string): Promise<OobSecondaryIdentityConfirmationDto> {
  const { data } = await apiClient.post<OobSecondaryIdentityConfirmationDto>(
    `/api/oob/${eventGuid}/secondary-identity/confirm`
  );
  return data;
}

export async function getOobEventParticipants(eventGuid: string): Promise<OobEventParticipantDto[]> {
  const { data } = await apiClient.get<OobEventParticipantDto[]>(`/api/admin/oob/events/${eventGuid}/participants`);
  return data;
}

export async function getOobParticipantGroups(): Promise<OobParticipantGroupDto[]> {
  const { data } = await apiClient.get<OobParticipantGroupDto[]>("/api/admin/oob/groups");
  return data;
}

export async function createOobParticipantGroup(payload: OobCreateParticipantGroupRequest): Promise<OobParticipantGroupDto> {
  const { data } = await apiClient.post<OobParticipantGroupDto>("/api/admin/oob/groups", payload);
  return data;
}

export async function getOobParticipantGroupMembers(groupGuid: string): Promise<OobParticipantGroupMemberDto[]> {
  const { data } = await apiClient.get<OobParticipantGroupMemberDto[]>(`/api/admin/oob/groups/${groupGuid}/members`);
  return data;
}

export async function queueOobPlatformAnnouncement(payload: OobCreatePlatformAnnouncementRequest): Promise<OobPlatformAnnouncementResult> {
  const { data } = await apiClient.post<OobPlatformAnnouncementResult>("/api/admin/oob/announcements/platform", payload);
  return data;
}

export async function getAdminOobPlatformAnnouncements(skip: number = 0, take: number = 50): Promise<OobPlatformAnnouncementListDto> {
  const { data } = await apiClient.get<OobPlatformAnnouncementListDto>("/api/admin/oob/announcements/platform", {
    params: { skip, take }
  });
  return data;
}

export async function addOobEventParticipant(eventGuid: string, participantGuid: string): Promise<void> {
  await apiClient.post(`/api/admin/oob/events/${eventGuid}/participants`, { participantGuid });
}

export async function removeOobEventParticipant(eventGuid: string, participantGuid: string): Promise<void> {
  await apiClient.delete(`/api/admin/oob/events/${eventGuid}/participants/${participantGuid}`);
}

export async function bulkAddOobEventParticipants(eventGuid: string, participantGuids: string[]): Promise<void> {
  await apiClient.post(`/api/admin/oob/events/${eventGuid}/participants/bulk-add`, participantGuids);
}

export async function getMyOobAdminEmergencyProfile(): Promise<OobAdminEmergencyProfileDto> {
  const { data } = await apiClient.get<OobAdminEmergencyProfileDto>("/api/admin/oob/admin-emergency-profile/me");
  return data;
}

export async function updateMyOobAdminEmergencyProfile(
  payload: OobUpdateAdminEmergencyProfileRequest
): Promise<OobAdminEmergencyProfileDto> {
  const { data } = await apiClient.put<OobAdminEmergencyProfileDto>("/api/admin/oob/admin-emergency-profile/me", payload);
  return data;
}

export interface OobBreakGlassRenewResponse {
  adminSessionToken: string;
  sessionExpiresAt: string;
}

export async function renewOobBreakGlassSession(): Promise<OobBreakGlassRenewResponse> {
  const { data } = await apiClient.post<OobBreakGlassRenewResponse>("/api/oob/breakglass/renew");
  return data;
}
